import React from "react";
import { Alert, Button, Card, Space, Typography } from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { StatusSolicitacaoReservaFront } from "../components/SolicitacaoReservaStatusTag";
import { formatDate } from "../../../uteis/dateUtils";

const { Title, Text, Paragraph } = Typography;

type Props = {
  status: Extract<
    StatusSolicitacaoReservaFront,
    "SEM_INTERESSE_PERIODO_PROCURADO" | "SEM_INTERESSE_PERIODO_ENCONTRADO"
  >;
  empreendimento?: string | null;
  periodoProcuradoIni?: string | null;
  periodoProcuradoFim?: string | null;
  onBuscarOutroPeriodo: () => void;
};

function renderPeriodo(inicio?: string | null, fim?: string | null) {
  if (!inicio && !fim) return "-";
  if (inicio && fim) return `${formatDate(inicio)} até ${formatDate(fim)}`;
  return formatDate(inicio ?? fim ?? "");
}

export default function StepAgradecimentoSemInteresse({
  status,
  empreendimento,
  periodoProcuradoIni,
  periodoProcuradoFim,
  onBuscarOutroPeriodo,
}: Props) {
  const procurandoOutroPeriodo =
    status === "SEM_INTERESSE_PERIODO_ENCONTRADO";

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <Space align="start" size={14}>
            <CheckCircleOutlined
              style={{ fontSize: 28, color: "#52c41a", marginTop: 4 }}
            />
            <div>
              <Title level={2} style={{ marginBottom: 8 }}>
                {procurandoOutroPeriodo
                  ? "Obrigado! Atualizamos sua solicitação."
                  : "Tudo certo! Encerramos esta procura."}
              </Title>
              <Paragraph style={{ marginBottom: 0, fontSize: 16 }}>
                {procurandoOutroPeriodo
                  ? "Seguiremos acompanhando novas opções para você. Se quiser, você também pode iniciar agora uma nova busca com outro período."
                  : "Registramos que você não tem mais interesse neste período. Se mudar de ideia depois, pode iniciar uma nova busca quando quiser."}
              </Paragraph>
            </div>
          </Space>

          <Alert
            type="success"
            showIcon
            message={
              procurandoOutroPeriodo
                ? "Seu interesse continua ativo para novas datas."
                : "A busca deste período foi encerrada com sucesso."
            }
            description={
              procurandoOutroPeriodo
                ? "Quando surgir uma alternativa compatível, o time poderá seguir com o atendimento a partir da sua solicitação atual."
                : "Nenhuma outra ação é necessária neste momento."
            }
            style={{ borderRadius: 14 }}
          />

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <Text type="secondary">Empreendimento</Text>
                <Text strong>{empreendimento ?? "-"}</Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <Text type="secondary">
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  Período procurado
                </Text>
                <Text strong>{renderPeriodo(periodoProcuradoIni, periodoProcuradoFim)}</Text>
              </div>
            </Space>
          </Card>

          <Card
            bordered={false}
            style={{ borderRadius: 14, background: "rgba(24,144,255,0.06)" }}
            styles={{ body: { padding: 18 } }}
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Title level={4} style={{ margin: 0 }}>
                Se quiser procurar outro período, clique aqui
              </Title>
              <Text type="secondary">
                Vamos te levar para uma nova busca.
              </Text>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={onBuscarOutroPeriodo}
              >
                Procurar outro período
              </Button>
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
}
